import { Alert, Form, Modal, Select, Switch, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { showNotification } from "../../../utils/notification/show-notification"
import { getFolders } from "../../Files/api/folder.services.ts"
import type { FolderListItem } from "../../Files/types/folder.types.ts"
import { saveAttachmentToProjectFiles } from "../api/services.ts"
import type { MessageAttachment } from "../types/messaging.types.ts"

interface SaveToProjectFilesModalProps {
  attachment: MessageAttachment
  projectId: string
  isCustomerConversation: boolean
  onClose: () => void
}

/**
 * Filing a chat attachment into Project Files.
 *
 * A destination is required rather than defaulted, and customer visibility is
 * a deliberate switch that starts off — the action creates a *new* document,
 * and nothing about the files already in that folder changes either way.
 */
export function SaveToProjectFilesModal({
  attachment,
  projectId,
  isCustomerConversation,
  onClose
}: SaveToProjectFilesModalProps) {
  const { t } = useTranslation()
  const [folders, setFolders] = useState<FolderListItem[]>([])
  const [loadingFolders, setLoadingFolders] = useState(true)
  const [folderId, setFolderId] = useState<string | undefined>()
  const [visibleToCustomer, setVisibleToCustomer] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const response = await getFolders({ project: projectId, page_size: 200 })
      if (cancelled) return
      setLoadingFolders(false)
      if (response.ok && response.data) setFolders(response.data.results)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [projectId])

  const submit = async () => {
    if (!folderId) return
    setSaving(true)
    const response = await saveAttachmentToProjectFiles(attachment.id, {
      folder: folderId,
      visible_to_customer: visibleToCustomer
    })
    setSaving(false)
    if (!response.ok) {
      showNotification.error({
        message: t(
          "messages.saveToFiles.failed",
          "Could not save this file to Project Files."
        )
      })
      return
    }
    showNotification.success({
      message: t("messages.saveToFiles.saved", "Saved to Project Files")
    })
    onClose()
  }

  return (
    <Modal
      open
      title={t("messages.saveToFiles.title", "Save to Project Files")}
      okText={t("common.save", "Save")}
      cancelText={t("common.cancel", "Cancel")}
      okButtonProps={{ disabled: !folderId, loading: saving }}
      onOk={submit}
      onCancel={onClose}
    >
      <Typography.Paragraph type={"secondary"}>
        {t(
          "messages.saveToFiles.explanation",
          "This creates a separate document in Project Files. The file stays in this conversation as well, and deleting the message will not remove the filed copy."
        )}
      </Typography.Paragraph>

      <Form layout={"vertical"}>
        <Form.Item
          required
          label={t("messages.saveToFiles.folder", "Destination folder")}
        >
          <Select
            showSearch
            loading={loadingFolders}
            value={folderId}
            onChange={setFolderId}
            placeholder={t(
              "messages.saveToFiles.folderPlaceholder",
              "Choose a folder"
            )}
            optionFilterProp={"label"}
            options={folders.map((folder) => ({
              value: folder.id,
              label: folder.name
            }))}
            notFoundContent={t(
              "messages.saveToFiles.noFolders",
              "This project has no folders yet. Create one in Files first."
            )}
          />
        </Form.Item>

        <Form.Item
          label={t(
            "messages.saveToFiles.customerVisible",
            "Visible to the customer"
          )}
        >
          <Switch
            checked={visibleToCustomer}
            onChange={setVisibleToCustomer}
            aria-label={t(
              "messages.saveToFiles.customerVisible",
              "Visible to the customer"
            )}
          />
        </Form.Item>
      </Form>

      {!isCustomerConversation && visibleToCustomer && (
        <Alert
          type={"warning"}
          showIcon
          message={t(
            "messages.saveToFiles.internalWarning",
            "This file came from an internal conversation. Sharing it makes it visible to the customer."
          )}
        />
      )}
    </Modal>
  )
}
